import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search } from 'lucide-react';
import { ConfirmModal } from '../../components/ConfirmModal';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../utils/api';

const ROLES = ['', 'COMPANY_ADMIN', 'DISPATCH_AGENT', 'RECOVERY_OFFICER', 'COMPANY_AGENT'];

export const CompanyAdminUsers: React.FC = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [users, setUsers] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [statusTarget, setStatusTarget] = useState<any>(null);

  const load = useCallback(async () => {
    if (!user?.companyId) return;
    setLoading(true);
    try {
      const res = await apiClient.listUsers(user.companyId, 100, 0);
      const d = res.data.data;
      setUsers(d?.users ?? d?.rows ?? (Array.isArray(d) ? d : []));
    } catch {} finally { setLoading(false); }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const confirmToggleStatus = async () => {
    if (!statusTarget) return;
    const next = statusTarget.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    try {
      await apiClient.updateUser(statusTarget.id, { status: next });
      toast(`User ${next === 'ACTIVE' ? 'activated' : 'deactivated'}`, 'success');
      load();
    } catch (e: any) { toast(e.response?.data?.error || 'Failed', 'error'); }
    finally { setStatusTarget(null); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await apiClient.deleteUser(deleteTarget.id);
      toast('User deleted', 'success');
      load();
    } catch (e: any) { toast(e.response?.data?.error || 'Failed to delete user', 'error'); }
    finally { setDeleteTarget(null); }
  };

  const filtered = users.filter(u => {
    const matchSearch = !search || `${u.firstName} ${u.lastName} ${u.email}`.toLowerCase().includes(search.toLowerCase());
    const matchRole = !roleFilter || u.role === roleFilter;
    return matchSearch && matchRole;
  });

  return (
    <>
      {statusTarget && (
        <ConfirmModal
          title={statusTarget.status === 'ACTIVE' ? 'Deactivate User' : 'Activate User'}
          message={`${statusTarget.status === 'ACTIVE' ? 'Deactivate' : 'Activate'} ${statusTarget.firstName} ${statusTarget.lastName}?`}
          confirmLabel={statusTarget.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
          danger={statusTarget.status === 'ACTIVE'}
          onConfirm={confirmToggleStatus}
          onCancel={() => setStatusTarget(null)}
        />
      )}
      {deleteTarget && (
        <ConfirmModal
          title="Delete User"
          message={`Permanently delete ${deleteTarget.firstName} ${deleteTarget.lastName}? This cannot be undone.`}
          confirmLabel="Delete"
          danger
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      <div className="page-header">
        <h1 className="page-title">Staff Members</h1>
        <Link to="/company-admin/users/new" className="btn btn-primary"><Plus size={16} /> New User</Link>
      </div>

      <div className="card">
        <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <Search size={15} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            <input placeholder="Search by name or email..." value={search} onChange={e => setSearch(e.target.value)} style={{ paddingLeft: 32, width: '100%' }} />
          </div>
          <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} style={{ width: 200 }}>
            {ROLES.map(r => <option key={r} value={r}>{r || 'All Roles'}</option>)}
          </select>
        </div>

        {loading ? <div className="loading-spinner">Loading users...</div> : filtered.length === 0 ? (
          <div className="empty-state"><h3>No users found</h3></div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Branch</th><th>Status</th><th>Last Login</th><th>Actions</th></tr></thead>
              <tbody>
                {filtered.map((u: any) => (
                  <tr key={u.id}>
                    <td style={{ fontWeight: 500 }}>{u.firstName} {u.lastName}</td>
                    <td>{u.email}</td>
                    <td><span className="badge badge-inactive" style={{ fontSize: 10 }}>{u.role.replace(/_/g, ' ')}</span></td>
                    <td>{u.branch?.name ?? '—'}</td>
                    <td><span className={`badge badge-${u.status?.toLowerCase()}`}>{u.status}</span></td>
                    <td style={{ fontSize: 12, color: '#94a3b8' }}>{u.lastLogin ? new Date(u.lastLogin).toLocaleDateString() : '—'}</td>
                    <td style={{ display: 'flex', gap: 6 }}>
                      <Link to={`/company-admin/users/${u.id}/edit`} className="btn btn-ghost" style={{ padding: '3px 8px', fontSize: 12 }}>Edit</Link>
                      <button
                        className={`btn ${u.status === 'ACTIVE' ? 'btn-danger' : 'btn-success'}`}
                        style={{ padding: '3px 8px', fontSize: 12 }}
                        onClick={() => setStatusTarget(u)}
                      >
                        {u.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        className="btn btn-ghost"
                        style={{ padding: '3px 8px', fontSize: 12, color: '#ef4444' }}
                        onClick={() => setDeleteTarget(u)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};
